import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import { useAuth } from '../../context/AuthContext';
import { favoritesApi } from '../../services/apiService';
import { shouldShowVerifiedBadge } from '../../utils/membershipUtils';
import VerificationBadge from '../../components/VerificationBadge/VerificationBadge';
import { API_BASE_URL } from '../../config/api';
import Pagination from '../../components/Pagination/Pagination';
import './TopSellerPage.css';

const SELLERS_PER_PAGE = 24; // 6 rows × 4 columns on desktop

const TopSellerPage = () => {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [sellers, setSellers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [favoriteIds, setFavoriteIds] = useState([]);
  const { user, isAuthenticated } = useAuth();

  // Pagination calculations
  const totalPages = Math.ceil(sellers.length / SELLERS_PER_PAGE);
  const startIndex = (currentPage - 1) * SELLERS_PER_PAGE;
  const currentSellers = sellers.slice(startIndex, startIndex + SELLERS_PER_PAGE);

  // Scroll to top on page change
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentPage]);

  // Fetch sellers from API
  useEffect(() => {
    const fetchSellers = async () => {
      try {
        console.log('Fetching sellers from:', `${API_BASE_URL}/sellers`);
        const response = await fetch(`${API_BASE_URL}/sellers`);
        const data = await response.json();
        console.log('Sellers API response:', data);
        // Handle both array response and object with sellers property
        const sellersArray = Array.isArray(data) ? data : (data.sellers || []);
        // Safety filter: only show published sellers on frontend
        const publishedSellers = sellersArray.filter(s => s.status === 'published');
        console.log('Sellers to display:', publishedSellers.length);
        setSellers(publishedSellers);
      } catch (error) {
        console.error('Error fetching sellers:', error);
        // Fallback to sample data
        setSellers(Array(20).fill(null).map((_, index) => ({
          id: index + 1,
          name: 'Emato International',
          companyName: 'Emato International',
          logo: `/uploads/builder-images/d5e646fb3e0bfda819927082f350da22c13d7aa4_w646.png`,
          isGlobal: true,
          isVerified: true,
          country: 'United State',
          memberYears: 1,
          businessType: 'Manufacture',
          reviews: 0,
          products: 20
        })));
      } finally {
        setLoading(false);
      }
    };
    fetchSellers();
  }, []);

  // Fetch user's favorite seller IDs
  useEffect(() => {
    const fetchFavoriteIds = async () => {
      if (user?.id) {
        try {
          const response = await favoritesApi.getIds(user.id);
          setFavoriteIds(response.sellerIds || []);
        } catch (error) {
          console.error('Error fetching favorite IDs:', error);
        }
      }
    };
    fetchFavoriteIds();
  }, [user?.id]);

  // Toggle favorite
  const handleToggleFavorite = async (sellerId) => {
    if (!isAuthenticated) {
      alert('Please login to save favorites');
      return;
    }

    try {
      const response = await favoritesApi.toggle(user.id, sellerId);
      if (response.isFavorited) {
        setFavoriteIds(prev => [...prev, sellerId]);
      } else {
        setFavoriteIds(prev => prev.filter(id => id !== sellerId));
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
    }
  };

  const SellerCard = ({ seller }) => {
    const isFavorited = favoriteIds.includes(seller.id);
    const displayName = seller.name || seller.companyName || 'Unknown Seller';
    const productCount = seller._count?.products || seller.products || 0;
    
    // Handle card click to navigate to seller profile
    const handleCardClick = (e) => {
      // Don't navigate if clicking on favorite button
      if (e.target.closest('button')) return;
      navigate(`/seller/${seller.id}`);
    };
    
    return (
      <div className="top-seller-card" onClick={handleCardClick} style={{ cursor: 'pointer' }}>
        {/* Background Image Container */}
        <div className="top-seller-image-container">
          <img 
            src={seller.coverImage || "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=600"} 
            alt="Seller Background" 
            className="top-seller-bg-image"
            onError={(e) => { e.target.onerror = null; e.target.src = 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=600'; }}
          />
        </div>
        
        {/* Seller Content Area */}
        <div className="top-seller-content">
          {/* Avatar + Name + Bookmark Container */}
          <div className="top-seller-avatar-row">
            {seller.logo ? (
              <img src={seller.logo} alt={displayName} className="top-seller-avatar" style={{width: 56, height: 56, borderRadius: '50%', objectFit: 'cover'}} />
            ) : (
              <svg className="top-seller-avatar" width="56" height="56" viewBox="0 0 56 56" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="28" cy="28" r="27.5" fill="#E6EBEF" stroke="#D3D3D3"/>
                <rect x="15" y="18" width="26" height="20" rx="2" fill="#1F396C"/>
                <circle cx="28" cy="28" r="6" fill="white"/>
              </svg>
            )}
            <div className="top-seller-name-wrap">
              <span className="top-seller-name">{displayName}</span>
            </div>
            {/* Bookmark Icon - Next to company name */}
            <button 
              className={`top-seller-bookmark-btn ${isFavorited ? 'favorited' : ''}`}
              onClick={(e) => {
                e.stopPropagation();
                handleToggleFavorite(seller.id);
              }}
              title={isFavorited ? 'Remove from favorites' : 'Add to favorites'}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M22 0H11.5C11.1022 0 10.7206 0.158035 10.4393 0.43934C10.158 0.720644 10 1.10218 10 1.5V18C10.0001 18.1338 10.036 18.2652 10.1039 18.3805C10.1719 18.4958 10.2695 18.5908 10.3866 18.6557C10.5037 18.7206 10.636 18.7529 10.7698 18.7494C10.9036 18.7458 11.034 18.7066 11.1475 18.6356L16.75 15.1341L22.3534 18.6356C22.4669 18.7063 22.5972 18.7454 22.7309 18.7488C22.8646 18.7522 22.9967 18.7198 23.1136 18.655C23.2306 18.5902 23.3281 18.4953 23.396 18.3801C23.4639 18.2649 23.4998 18.1337 23.5 18V1.5C23.5 1.10218 23.342 0.720644 23.0607 0.43934C22.7794 0.158035 22.3978 0 22 0ZM22 16.6472L17.1466 13.6144C17.0274 13.5399 16.8896 13.5004 16.7491 13.5004C16.6085 13.5004 16.4708 13.5399 16.3516 13.6144L11.5 16.6472V1.5H22V16.6472Z" 
                  fill={isFavorited ? '#1F396C' : '#969696'}
                />
              </svg>
            </button>
          </div>
          
          {/* Info Section - Badges, Location, Business Type */}
          <div className="top-seller-info-section">
            {/* Badges Row */}
            <div className="top-seller-badges-row">
              {/* Membership Plan Badge */}
              {(() => {
                const plan = seller.membershipPlan || 'Basic';
                const planLower = plan.toLowerCase();
                
                const basicIcon = '/uploads/builder-images/d8ee44da49d1e0c28cf5bfe995ca6aa7f62420ea_w44.png';
                const gccIcon = '/uploads/builder-images/cdcdf2f44d98a4cc6cdc74b524bc4359f623ed6c_w44.png';
                const globalIcon = '/uploads/builder-images/050559d54da8d35ba6d11841efdb737d4c3ae660_w44.png';
                
                let config;
                
                if (planLower.includes('gcc') || planLower.includes('premium') || planLower.includes('gold')) {
                  config = { color: '#009D0A', icon: gccIcon, label: 'GCC Business' };
                } else if (planLower.includes('global') || planLower.includes('enterprise') || planLower.includes('platinum')) {
                  config = { color: '#FF6B00', icon: globalIcon, label: 'Global' };
                } else {
                  config = { color: '#6D6D6D', icon: basicIcon, label: 'Basic' };
                }
                
                return (
                  <div className="top-seller-global-badge">
                    {config.icon && (
                      <img src={config.icon} alt={config.label} className="top-seller-global-icon" />
                    )}
                    <span className="top-seller-global-text" style={{ color: config.color }}>{config.label}</span>
                  </div>
                );
              })()}
            </div>
              {/* Verification Badge - Based on verificationLevel */}
              <VerificationBadge level={seller.verificationLevel || (seller.isVerified ? 'verified' : 'pending')} size="md" />
            
            {/* Location with Flag */}
            <div className="top-seller-location-wrap">
              <div className="top-seller-location-row">
                <div className="top-seller-flag-container">
                  <img src={seller.countryFlag || "https://flagcdn.com/w80/ae.png"} alt="Flag" className="top-seller-flag-img" />
                </div>
                <span className="top-seller-location-text">
                  <span className="top-seller-country">{seller.country || 'United State'} / </span>
                  <span className="top-seller-years">{seller.memberYears || 1} Year{(seller.memberYears || 1) > 1 ? 's' : ''}</span>
                </span>
              </div>
            </div>
            
            {/* Business Type */}
            <div className="top-seller-business-wrap">
              <div className="top-seller-business-inner">
                <span className="top-seller-business-type">{seller.businessType || 'Manufacturer'}</span>
              </div>
            </div>
          </div>
          
          {/* Stats Row */}
          <div className="top-seller-stats-row">
            <div 
              className="top-seller-stat-item" 
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/seller/${seller.id}?tab=reviews`);
              }}
              style={{ cursor: 'pointer' }}
              title="View seller reviews"
            >
              <span className="top-seller-stat-text">{seller.reviews || 0} Reviews</span>
            </div>
            <div 
              className="top-seller-stat-item"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/supplier/${seller.id}/products`);
              }}
              style={{ cursor: 'pointer' }}
              title="View seller products"
            >
              <span className="top-seller-stat-text">{productCount} Products</span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="top-seller-page">
      <Header />
      
      <main className="top-seller-main">
        <div className="top-seller-container">
          {/* Breadcrumb */}
          <div className="top-seller-breadcrumb">
            <span 
              className="breadcrumb-item" 
              onClick={() => navigate('/')}
              style={{ cursor: 'pointer' }}
            >
              Home
            </span>
            <svg className="breadcrumb-chevron" width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M6 13L10 9L6 5" stroke="#505050" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span className="breadcrumb-item active">Top seller</span>
          </div>
          
          {/* Page Header */}
          <div className="top-seller-header">
            <div className="top-seller-title-section">
              <h1 className="top-seller-title">Top Supplier</h1>
              <p className="top-seller-subtitle">Discover the most trusted suppliers chosen by thousands of buyers.</p>
            </div>
          </div>
          
          {/* Sellers Grid */}
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
              Loading suppliers...
            </div>
          ) : sellers.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
              No suppliers found
            </div>
          ) : (
            <div className="top-seller-grid">
              {currentSellers.map((seller) => (
                <SellerCard key={seller.id} seller={seller} />
              ))}
            </div>
          )}
          
          {/* Pagination */}
          {totalPages > 1 && (
            <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
          )}
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default TopSellerPage;
